import { useQuery } from '@tanstack/react-query';
import {
  ActiveSourcingChannelDetails,
  SourcingService,
} from 'api-clients/monolith';
import { cn } from '@/utils/classnames';
import _range from 'lodash/range';
import { useEffect, useMemo } from 'react';

import { resolveMetricsApiDateParams } from '@/components/pages/opening-details/overview/date-range-view/filter-utils';
import { useLocalDateRanges } from '@/hooks/use-local-date-ranges';
import { Skeleton } from '@/components/ui/skeleton';
import { useOpeningContext } from '@/contexts/opening-context';
import { useDataTable } from '@/hooks/use-data-table';
import { queryKeys } from '@/query-keys';

import { CampaignRowLayout } from './campaign-row-layout';
import { useChannelColumns } from './columns';
import { useReachChannels } from './use-reach-channels';

interface ReachChannelsProps {
  sourcingPurchaseId: string;
}

export function ReachChannels({ sourcingPurchaseId }: ReachChannelsProps) {
  const { reachPublishers, setReachPublishers, setReachPublisherStatus } =
    useReachChannels();
  const { hiringGoalId, startDate, endDate, filterRange, displayPrefs } =
    useOpeningContext();
  const localDays = useLocalDateRanges();
  const metricsDates = useMemo(
    () =>
      resolveMetricsApiDateParams({
        startDate,
        endDate,
        filterRange,
        localDays,
      }),
    [startDate, endDate, filterRange, localDays],
  );

  const { data, isError, isFetching } = useQuery({
    queryKey: [
      ...queryKeys.openingDetails.channels.reach,
      sourcingPurchaseId,
      displayPrefs.showAggregateHires,
      hiringGoalId,
      metricsDates.startDate,
      metricsDates.endDate,
      metricsDates.filterRange,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingPurchasesCampaignDetails(
        sourcingPurchaseId,
        displayPrefs.showAggregateHires,
        hiringGoalId,
        metricsDates.startDate,
        metricsDates.endDate,
        metricsDates.filterRange,
      ),
  });

  if (isError) {
    throw new Error('Error fetching channels');
  }

  const campaigns = useMemo(() => data?.reach_campaigns ?? [], [data]);

  useEffect(() => {
    setReachPublishers(campaigns);
  }, [campaigns, setReachPublishers]);

  const columns = useChannelColumns({
    setReachPublisherStatus,
  });

  const { table } = useDataTable<ActiveSourcingChannelDetails>({
    columns,
    data: reachPublishers,
    rowCount: reachPublishers.length,
  });

  if (isFetching)
    return <ReachChannelsSkeleton columnsLength={columns.length} />;

  return (
    <>
      {table.getRowModel().rows.map((row, index) => {
        const cells = row.getVisibleCells();
        const actionsCell = cells.find(c => c.column.id === 'actions');
        const channelCell = cells.find(c => c.column.id === 'sourcing_channel');
        const allMetricCells = cells.filter(
          c => c.column.id !== 'actions' && c.column.id !== 'sourcing_channel',
        );

        return (
          <CampaignRowLayout
            key={row.id}
            actionsCell={actionsCell}
            channelCell={channelCell}
            metricCells={allMetricCells}
            hideActionsDivider
            className={cn(
              'bg-fx-neutral-100',
              index > 0 && 'border-fx-neutral-200 border-t',
            )}
          />
        );
      })}
    </>
  );
}

function ReachChannelsSkeleton({ columnsLength }: { columnsLength: number }) {
  return (
    <>
      {_range(2).map(rowIndex => (
        <div
          key={rowIndex}
          className={cn(
            'bg-fx-neutral-100 flex items-stretch',
            rowIndex > 0 && 'border-fx-neutral-200 border-t',
          )}
        >
          {/* Left actions panel — no border-r, matches hideActionsDivider on real tiles */}
          <div className="flex shrink-0 items-center justify-center self-stretch p-4">
            <Skeleton className="h-6 w-6 rounded" />
          </div>

          <div className="flex min-w-0 flex-1 items-stretch">
            {/* Publisher info */}
            <div className="flex w-[216px] shrink-0 items-center gap-3 px-4 py-3">
              <Skeleton className="h-6 w-6 shrink-0 rounded-full" />
              <Skeleton className="h-5 w-20" />
            </div>

            <div className="bg-fx-neutral-200 my-4 w-px shrink-0" />

            {/* Metric columns */}
            <div
              className="grid min-w-0 flex-1"
              style={{
                gridTemplateColumns: `repeat(${columnsLength - 2}, minmax(120px, 1fr))`,
              }}
            >
              {_range(columnsLength - 2).map(colIndex => (
                <div
                  key={colIndex}
                  className="flex flex-col items-center gap-3 px-4 py-3"
                >
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-6 w-12" />
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </>
  );
}
